import React from "react";
import { useSelector } from "react-redux";
import { Backdrop, Card, CardContent, Grid, makeStyles } from "@material-ui/core";
import { Formik } from "formik";
import * as Yup from "yup";
import TestingProcessDialog from "../../../components/client/Estimation/TestingProcessDialog";
import {
    SubmitEstimationFormikProps,
    submitIULEstimation,
} from "../../client/Estimation/V1/Components/EstimationFormikHandler";
import ViewFilesList from "../../client/Estimation/V1/Components/DialogBox/ViewFilesList";
import Socket from "../../client/Estimation/V1/Socket";
import { IULBenefits as IULBenefitsCard, IULEstimatorChildForm } from "./index";
import { useCreateAllocationPayLoad } from "../../client/Estimation/Hooks/Hooks";
import { RootState } from "../../../reducers";
import * as EstimatorActions from "../../../actions/estimator";
import * as EstimationActions from "../../../actions/estimation";
import { useActions } from "../../../actions";
import { isDebug, joinAsFullName, maskCurrency, noop, sleep, unmaskCurrency } from "../../../utils/commonUtils";
import { CallingFromObject, ESTIMATE_MAX_AGE, NationalLifeCarrierDetail } from "../../../constants";
import { getQueryStringValue } from "../../../utils/queryString";
import CommonLoader from "../../../components/CommonLoader";


const useStyles = makeStyles((theme) => ({
    cardContainer: {
        border: "1px solid #0096ff",
        borderRadius: "12px",
        padding: 0,
    },
    rightPanelWrap: {
        backgroundColor: theme.palette.primary.main,
        color: "#ffffff !important",
    },
    resultSection: {
        marginBottom: theme.spacing(3),
        "&:last-child": {
            marginBottom: 0,
        },
    },
    resultValue: {
        fontWeight: "bold",
        fontSize: "2.5rem",
        lineHeight: 1.2,
    },
    resultLabel: {
        opacity: 0.9,
    },
    formLabel: {
        fontWeight: 500,
        marginBottom: theme.spacing(1),
        display: "block",
    },
    calculateButton: {
        backgroundColor: "#0096ff",
        color: "#ffffff",
        borderRadius: "50px",
        padding: theme.spacing(1.5, 4),
        fontSize: "1rem",
        fontWeight: "bold",
        marginTop: theme.spacing(3),
        "&:hover": {
            backgroundColor: "#0077cc",
        },
    },
    sliderContainer: {
        paddingTop: theme.spacing(2),
    },
    sliderLabels: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: theme.spacing(-1),
    },
}));


type IULEstimatorFormProps = {
    setIllustrationId: Function;
    getStepperCurrentState: Function;
    verifyInviteData?:any
}


const IULEstimatorForm: React.FC<IULEstimatorFormProps> = (props) => {
    const { getStepperCurrentState, setIllustrationId, verifyInviteData } = props;

    const callingFrom: string = CallingFromObject.ENROLLMENT;

    const classes = useStyles();
    const debugMode = isDebug();

    const estimatorActions = useActions(EstimatorActions);
    const estimationActions = useActions(EstimationActions);

    const userAuth = useSelector((state: RootState) => state.auth);
    const minCon = useSelector((state: RootState) => state.minimumContribution) || 21350;

    const inviteToken = getQueryStringValue("token");

    const getClientEstimationData = async () => {
        if (!clientPolicy) {
            debugMode && console.error("No client policy found.");
            return;
        }

        const userId = user.id;
        const clientPolicyId = clientPolicy.id;
        setLoadingEstimationData(true);
        debugMode && await sleep(3000);

        const estimationDetails: any = await estimationActions.getEstimationDetailsByUserId(userId, callingFrom, clientPolicyId);
        if (!estimationDetails || !estimationDetails.id) {
            setLoadingEstimationData(false);
            debugMode && console.error("No estimation details found for userId:", userId, "and clientPolicyId:", clientPolicyId);
            return;
        }

        debugMode && console.log(estimationDetails && estimationDetails.id, "estimationDetails");

        if (!estimationDetails.stressResponse) {
            setLoadingEstimationData(false);
            debugMode && console.error("No stress response found in estimation details for userId:", userId, "and clientPolicyId:", clientPolicyId);
            return;
        }

        const stressResponse: any = JSON.parse(estimationDetails && estimationDetails.stressResponse);

        if (!stressResponse && !stressResponse.success) {
            setLoadingEstimationData(false);
            debugMode && console.error("No success response found in estimation details for userId:", userId, "and clientPolicyId:", clientPolicyId);
            return;
        }

        debugMode && console.log(estimationDetails, "responsePayload");

        const successResponse: any = stressResponse && stressResponse.success;

        debugMode && console.table(successResponse);
        setEstimationResponse(successResponse);

        setLoadingEstimationData(false);
    };

    const user: any = !inviteToken && userAuth ? userAuth.user : verifyInviteData;
    const client = !inviteToken && userAuth ? user.client : verifyInviteData;

    const [loading, setLoading] = React.useState(true);
    const [issueAge, setIssueAge] = React.useState<number>((client && client.age) as number);
    const [estimatorData, setEstimatorData] = React.useState<any>(null);
    const [estimationResponse, setEstimationResponse] = React.useState<any>(null);
    const [requestId, setRequestId] = React.useState<null | string>(null);
    const [isEstimationDataLoading, setIsEstimationDataLoading] = React.useState<boolean>(false);
    const [loadingEstimationData, setLoadingEstimationData] = React.useState<boolean>(false);
    const [isReportDataLoading, setIsReportDataLoading] = React.useState<boolean>(false);
    const [isReportRequestCompleted, setIsReportRequestCompleted] = React.useState<boolean>(false);

    const clientPolicy = !inviteToken && userAuth ? client && client.clientPolicies && client.clientPolicies.length > 0 && client.clientPolicies[0] : verifyInviteData.client;
    const carrierId = clientPolicy && clientPolicy.carrier && clientPolicy.strategy ? clientPolicy.carrier.id : NationalLifeCarrierDetail.value;
    const strategyId = clientPolicy && clientPolicy.strategy && clientPolicy.strategy.id;
    const isNfIul = clientPolicy && clientPolicy.strategy && clientPolicy.strategy.slug === "iul";

    const initialValues = {
        /**
         * Default is the greater of the minimum value or 65.
         */
        beginDistributions: ESTIMATE_MAX_AGE,
        contributionAmount: "",
        contributionFrequency: "",
        /**
         * Default to 90. Minimum must be equal to minimum of Start Age.
         */
        endDistributions: 90,
        gender: client && client.gender,
        healthType: "",
        issueAge,
        primaryFocusPlan: "",
        tobacco: "",
    };
    const validationSchema = Yup.object().shape({
        beginDistributions: Yup.number().required("Start age is required"),
        contributionAmount: Yup.string()
                .required("Contribution Amount is required")
                .test("min-amount", `Min Amount should be ${maskCurrency(minCon + "")}`, (value) => {
                    if (!value) return false;
                    const num = unmaskCurrency(value); // remove commas if formatted
                    return !isNaN(num) && num >= minCon;
                }),
        contributionFrequency: Yup.string().required("Premium schedule is required"),
        endDistributions: Yup.number().required("End age is required").min(Yup.ref("beginDistributions"), "End age can't be less than start age"),
        gender: Yup.number().required("Sex is required"),
        healthType: Yup.string().required("Health status is required"),
        issueAge: Yup.number().required("Age is required").min(18).max(70),
        primaryFocusPlan: Yup.string().required("Contribution Years required"),
        tobacco: Yup.string().required("This field is required"),
    });


    const createAllocationPayLoad = useCreateAllocationPayLoad((estimatorData && estimatorData.id), client.carrier.id, client.client.clientId, user, isNfIul) ;

    console.log(client,"cleint")
    console.log(createAllocationPayLoad,"createAllocationPayLoad")
    const onCompleteReportDataLoading = (estimationResults: any) => {
        const responseData: any = estimationResults;
        setIllustrationId && setIllustrationId(responseData && responseData.reports && responseData.reports.client && responseData.reports.client.illustrationId);
    };

    const socketButtonClick = () => {
        // const userObj = {
        //     clientPolicy,
        //     companyLinkUrl: clientPolicy && clientPolicy.company && clientPolicy.company.companyLinkUrl,
        //     id: user && user.id,
        //     loggerInUserId: user.id,
        //     role: user && user.role as number,
        // };
        // const payload: any = {
        //     callingFrom,
        //     createAllocationPayLoad,
        //     estimationActions,
        //     initRequest: noop,
        //     onReportsAlreadyExist: () => setIsEstimationDataLoading(false),
        //     onRequestIdCreated: (id: any) => setRequestId(id),
        //     setIsEstimationDataLoading,
        //     setIsReportDataLoading,
        //     setIsReportRequestCompleted,
        //     setRequestId,
        //     user: userObj,
        // };
        // createEstimateReports(payload).then();
    };

    const onHandleSubmit = async (values: any) => {
        // run new illustration
        const payload: SubmitEstimationFormikProps = {
            agentName: joinAsFullName(clientPolicy && clientPolicy.agent && clientPolicy.agent.user as any),
            atlantic: false,
            callingFrom,
            clientPolicy,
            createAllocationPayLoad,
            estimationActions,
            estimatorData,
            issueAge: issueAge,
            loggedInUser: user,
            role: user.role,
            setIsEstimationDataLoading,
            setRequestId,
            user: user,
            userId: user.id,
            userName: joinAsFullName(user),
            values: {
                ...values,
                annualContribution: values.contributionAmount,
                carrierId,
                dob: client && client.dob,
                InvestmentType: client && client.clientInvestmentType,
            },
        };
        debugMode && console.log(payload, payload.values);
        submitIULEstimation(payload).then();
    };

    React.useEffect(() => {
        (async () => {
            const response: any = await estimationActions.getIssueAge(client && client.dob, clientPolicy && clientPolicy.carrierId);
            setIssueAge(response && response.issueAge);
            setLoading(false);
        })();
    }, []);

    React.useEffect(() => {
        if (typeof getStepperCurrentState === "function") {
            getStepperCurrentState({
                setIsReportDataLoading,
                isReportRequestCompleted,
                responseData: estimationResponse,
                isReportDataLoading,
                setIsReportRequestCompleted,
                showResults: true,
            });
        }
    }, [estimationResponse, isReportDataLoading, isReportRequestCompleted, setIsReportDataLoading, setIsReportRequestCompleted]);

    React.useEffect(() => {
        (async () => {
            if (carrierId && strategyId) {
                const res = await estimatorActions.getEstimatorDetails({
                    carrierId: carrierId,
                    strategyId: strategyId,
                });
                if (!!res) {
                    setEstimatorData(res);
                }
            }
        })();
    }, [carrierId, strategyId]);

    React.useEffect(() => {
        (async () => {
            if (!isEstimationDataLoading) {
                await getClientEstimationData();
            }
        })();
    }, [isEstimationDataLoading]);

    if (loading) {
        return (
                <CommonLoader />
        );
    }

    return (
            <>
                <Socket
                        callingFrom={callingFrom}
                        closeAfterFullProcess={false}
                        handleButtonClick={socketButtonClick}
                        handleReset={noop}
                        handleResult={noop}
                        isReportRequestAutomated={false}
                        onCompleteReportDataLoading={onCompleteReportDataLoading}
                        page={"estimator"}
                        requestId={requestId}
                        setIsEstimationDataLoading={setIsEstimationDataLoading}
                        setIsReportDataLoading={setIsReportDataLoading}
                        setIsReportRequestCompleted={setIsReportRequestCompleted}
                        setLifeDeathBenefitSeeking={noop}
                        setResponseData={setEstimationResponse}
                        userId={user && user.id}
                />

                <Grid container spacing={2}>
                    <Grid item xs={12} md={12}>
                        <Card raised className={classes.cardContainer}>
                            <CardContent className="pb0">
                                <Grid container spacing={4}>
                                    {/* ---THE FORM --- */}
                                    <Formik
                                            initialValues={initialValues}
                                            validationSchema={validationSchema}
                                            onSubmit={onHandleSubmit}
                                    >
                                        <Grid item xs={12} md={7}>
                                            <IULEstimatorChildForm issueAge={issueAge} isNfIul={isNfIul}  />
                                        </Grid>
                                    </Formik>

                                    {/* ---THE RESULTS --- */}
                                    <Grid
                                            className={classes.rightPanelWrap}
                                            item
                                            md={5}
                                            xs={12}
                                    >
                                        <IULBenefitsCard isIul={true} responseData={estimationResponse} />

                                        {
                                                isReportRequestCompleted &&
                                                <ViewFilesList
                                                        callingFrom={callingFrom}
                                                        isReportRequestCompleted={isReportRequestCompleted}
                                                        setIsReportRequestCompleted={setIsReportRequestCompleted}
                                                        userId={user && user.id} // here userId is client id
                                                />
                                        }
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                </Grid>

                {
                        isEstimationDataLoading &&
                        <Backdrop className={"classes.backdrop"} open={isEstimationDataLoading}>
                            <TestingProcessDialog
                                    getEstimationProgress={estimationActions.getEstimationProgress}
                                    handleClose={() => setIsEstimationDataLoading(false)}
                                    isEstimationDataLoading={isEstimationDataLoading}
                                    open={isEstimationDataLoading}
                            />
                        </Backdrop>
                }
            </>
    );
};

export default IULEstimatorForm;
